import { ArrowLeft } from '@untitled-ui/icons-react/build/cjs';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ButtonCmp from 'src/components/ButtonCmp';
import ConfirmationModalCmp from 'src/components/ConfirmModal/ConfirmationModalCmp';
import PageSectionLayout from 'src/components/PageSectionLayout';
import Header from 'src/components/PageSectionLayout/Header/Header';
import StepperLayout from 'src/components/StepperLayout/StepperLayout';
import { QUOTE_CLASSIFICATION } from 'src/constants/common';
import { PATH } from 'src/constants/path';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import WalToast from 'src/utils/WalToast';

import { QUOTE_STEPS } from './createQuote.const';
import { IStepNew } from './createQuote.interface';
import { createQuoteCurrentStep, resetCreateQuoteData, divisionId, customerBillingAccountId, quoteDetails } from './CreateQuote.slice';
import Sidebar from './Sidebar';
import AddDimensions from './Steps/Dimension';
import FtlLoadShipmentDetails from './Steps/FtlLoadShipmentDetails';
import QuoteDetails from './Steps/QuoteDetails';
import ShipmentDetails from './Steps/ShipmentDetails';
import ShipperConsignee from './Steps/ShipperConsignee';

const NewCreateQuote = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const stepInfo = useAppSelector(createQuoteCurrentStep);
    const selectedDivisionId = useAppSelector(divisionId);
    const selectedCustomerBillingAccountId = useAppSelector(customerBillingAccountId);
    const currentQuoteDetails = useAppSelector(quoteDetails);
    const classification = useMemo(() => currentQuoteDetails?.classification ?? QUOTE_CLASSIFICATION.FTL, [currentQuoteDetails]);
    const [currentStep, setCurrentStep] = useState<IStepNew>(QUOTE_STEPS(t, classification)[0]);
    const [headerChildren, setHeaderChildren] = useState<React.ReactNode>(null);
    const [isShowLeaveConfirmation, setIsShowLeaveConfirmation] = useState(false);

    useEffect(() => {
        const step = QUOTE_STEPS(t, classification)?.find((s) => s.key === stepInfo);

        if (step) {
            setCurrentStep(step);
        }
    }, [stepInfo, classification]);

    useEffect(() => {
        if (!selectedDivisionId || !selectedCustomerBillingAccountId) {
            WalToast.error(t('QuotingHub.QuotingDashboard.Division_and_billing_account_required'));
            navigate(PATH.MY_QUOTE, { replace: true });
        }
    }, []);

    const renderStepComponent = () => {
        switch (currentStep.step) {
            case 1:
                return <QuoteDetails setHeaderChildren={setHeaderChildren} />;
            case 2:
                return <ShipperConsignee setHeaderChildren={setHeaderChildren} />;
            case 3:
                if (classification === 'ftl') {
                    return <FtlLoadShipmentDetails setHeaderChildren={setHeaderChildren} />;
                }

                return <ShipmentDetails setHeaderChildren={setHeaderChildren} />;
            case 4:
                return <AddDimensions setHeaderChildren={setHeaderChildren} />;
            default:
                return null;
        }
    };

    // The header arrow is the only control that abandons the whole stepper — the per-step Back
    // buttons just move between steps and must never prompt. So the confirmation lives here and
    // nowhere else.
    const handleBack = () => {
        setIsShowLeaveConfirmation(true);
    };

    const handleLeaveWithoutSaving = () => {
        setIsShowLeaveConfirmation(false);
        dispatch(resetCreateQuoteData());
        navigate(PATH.MY_QUOTE, { replace: true });
    };

    return (
        <PageSectionLayout
            headerClassName="stepper-container"
            contentClassName="stepper-container overflow-y-auto"
            header={
                <Header
                    title={
                        <div className="flex items-center gap-x-2.5">
                            <ButtonCmp className="btn_secondary_black" size="square-sm" onClick={handleBack}>
                                <ArrowLeft className="w-4 h-4" />
                            </ButtonCmp>
                            {t('QuotingHub.Common.New_Quote')}
                        </div>
                    }
                />
            }
        >
            <StepperLayout
                Sidebar={<Sidebar currentStep={currentStep} classification={classification} />}
                title={currentStep.screenTitle}
                description={currentStep.screenDescription}
                headerChildren={headerChildren}
            >
                {renderStepComponent()}
            </StepperLayout>

            {isShowLeaveConfirmation && (
                <ConfirmationModalCmp
                    title={t('QuotingHub.Common.Discard_Changes')}
                    description={t('QuotingHub.Common.Discard_Changes_Desc')}
                    handleClose={() => setIsShowLeaveConfirmation(false)}
                    isPrimaryDangerBtn={true}
                    dangerBtnText={t('QuotingHub.Common.Leave_Without_Saving')}
                    handleSubmit={handleLeaveWithoutSaving}
                    secondaryBtnText={t('QuotingHub.Common.Stay')}
                    handleSecondaryBtn={() => setIsShowLeaveConfirmation(false)}
                />
            )}
        </PageSectionLayout>
    );
};

export default NewCreateQuote;
